import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppNavigation, { SYSTEM_ITEMS } from './AppNavigation';
import './AppShell.css';

const COMMANDS = [
    { to: '/workflow', label: 'Heute öffnen', hint: 'Fristen, Aufgaben und Entscheidungen' },
    { to: '/workflow#workflow-create', label: 'Neue Aufgabe oder Lead', hint: 'Anlagefunktionen in Heute' },
    { to: '/projects', label: 'Mandat finden', hint: 'Alle Förderfälle durchsuchen' },
    { to: '/crm', label: 'Kundenakte öffnen', hint: 'Accounts, Kontakte und Stammdaten' },
    { to: '/revenue', label: 'Umsatzchance bearbeiten', hint: 'Revenue Command Center' },
    { to: '/finance', label: 'Finanzen prüfen', hint: 'Provisionen, Termine und Belege' },
    { to: '/funding-database', label: 'Förderprogramm suchen', hint: 'System & Wissen' },
    { to: '/ai-configuration', label: 'KI-Konfiguration öffnen', hint: 'System & Wissen' },
    { to: '/schema-health', label: 'Systemcheck starten', hint: 'Datenstruktur prüfen' }
];

const AppShell = ({ children }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [commandOpen, setCommandOpen] = useState(false);
    const [commandQuery, setCommandQuery] = useState('');
    const [guideOpen, setGuideOpen] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.localStorage.getItem('foerderbuddyEliteOnboardingSeen') !== '1';
    });

    const closeGuide = () => {
        window.localStorage.setItem('foerderbuddyEliteOnboardingSeen', '1');
        setGuideOpen(false);
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setCommandOpen((current) => !current);
            }
            if (event.key === 'Escape') {
                setMobileMenuOpen(false);
                setCommandOpen(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const visibleCommands = COMMANDS.filter((command) => (
        `${command.label} ${command.hint}`.toLowerCase().includes(commandQuery.trim().toLowerCase())
    ));

    const closeCommand = () => {
        setCommandOpen(false);
        setCommandQuery('');
    };

    return (
        <div className="elite-app-shell">
            <aside className="elite-app-sidebar" aria-label="Förderbuddy Navigation">
                <Link to="/workflow" className="elite-app-brand">
                    <span className="elite-app-brand-mark" aria-hidden="true">FB</span>
                    <span>
                        <strong>Förderbuddy</strong>
                        <small>Mandatsbetriebssystem</small>
                    </span>
                </Link>
                <button type="button" className="elite-app-command-trigger" onClick={() => setCommandOpen(true)}>
                    <span>Suchen oder springen</span>
                    <kbd>⌘ K</kbd>
                </button>
                <AppNavigation />
                <div className="elite-app-help">
                    <strong>Hilfe benötigt?</strong>
                    <span>Jede Seite zeigt oben den nächsten richtigen Schritt.</span>
                    <button type="button" onClick={() => setGuideOpen(true)}>Kurzführung öffnen</button>
                </div>
            </aside>

            <div className="elite-app-stage">
                <header className="elite-app-mobile-header">
                    <Link to="/workflow" className="elite-app-mobile-brand">Förderbuddy</Link>
                    <button
                        type="button"
                        aria-expanded={mobileMenuOpen}
                        aria-controls="elite-mobile-menu"
                        onClick={() => setMobileMenuOpen((current) => !current)}
                    >
                        {mobileMenuOpen ? 'Schließen' : 'Menü'}
                    </button>
                    <button type="button" className="elite-app-mobile-search" onClick={() => setCommandOpen(true)} aria-label="Suchen oder springen">Suchen</button>
                </header>

                {mobileMenuOpen && (
                    <div id="elite-mobile-menu" className="elite-app-mobile-menu">
                        <AppNavigation onNavigate={() => setMobileMenuOpen(false)} />
                        <nav aria-label="System und Wissen">
                            <span>System & Wissen</span>
                            {SYSTEM_ITEMS.map((item) => (
                                <Link key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)}>{item.label}</Link>
                            ))}
                        </nav>
                    </div>
                )}

                <main className="elite-app-content">{children}</main>
            </div>

            <div className="elite-app-mobile-nav">
                <AppNavigation compact />
            </div>

            {guideOpen && (
                <div className="elite-guide-backdrop" role="presentation" onMouseDown={closeGuide}>
                    <section className="elite-guide" role="dialog" aria-modal="true" aria-labelledby="elite-guide-title" onMouseDown={(event) => event.stopPropagation()}>
                        <span className="elite-guide-kicker">Willkommen bei Förderbuddy</span>
                        <h2 id="elite-guide-title">In drei Schritten sicher arbeiten</h2>
                        <p>Förderbuddy zeigt nicht alles gleichzeitig. Beginne immer bei der nächsten sinnvollen Aktion.</p>
                        <ol>
                            <li><strong>Heute</strong><span>Hier startet dein Arbeitstag mit Fristen, Entscheidungen und Kundenrücklauf.</span></li>
                            <li><strong>Mandat öffnen</strong><span>Der Projektkopf zeigt Zustand, Verantwortlichen und den nächsten Schritt.</span></li>
                            <li><strong>Warum verstehen</strong><span>Status, Quellen und Diagnose bleiben erreichbar, ohne die tägliche Arbeit zu überlagern.</span></li>
                        </ol>
                        <div className="elite-guide-areas" aria-label="Förderbuddy Bereiche">
                            <span>Heute · Tagessteuerung</span>
                            <span>Mandate · Förderfälle</span>
                            <span>Kunden · Beziehungsakten</span>
                            <span>Umsatz · Chancen</span>
                            <span>Finanzen · Zahlungen</span>
                        </div>
                        <details className="elite-guide-glossary">
                            <summary>Fachbegriffe kurz erklärt</summary>
                            <dl>
                                <div><dt>Freigabeprüfung</dt><dd>Entscheidet, ob der nächste Prozessschritt fachlich zulässig ist.</dd></div>
                                <div><dt>Programmfit</dt><dd>Bewertet die Passung des Vorhabens zum Förderprogramm.</dd></div>
                                <div><dt>Kundeninput-Reife</dt><dd>Zeigt, ob die gelieferten Angaben belastbar weiterverarbeitet werden können.</dd></div>
                                <div><dt>Nachweisstatus</dt><dd>Zeigt Qualität und Vollständigkeit der entscheidungsrelevanten Quellen.</dd></div>
                            </dl>
                        </details>
                        <button type="button" onClick={closeGuide}>Förderbuddy starten</button>
                    </section>
                </div>
            )}

            {commandOpen && (
                <div className="elite-command-backdrop" role="presentation" onMouseDown={closeCommand}>
                    <section className="elite-command" role="dialog" aria-modal="true" aria-label="Förderbuddy durchsuchen" onMouseDown={(event) => event.stopPropagation()}>
                        <label>
                            <span>Suchen oder direkt springen</span>
                            <input
                                autoFocus
                                value={commandQuery}
                                onChange={(event) => setCommandQuery(event.target.value)}
                                placeholder="z. B. Mandat, Aufgabe, Finanzen…"
                            />
                        </label>
                        <div className="elite-command-results">
                            {visibleCommands.map((command) => (
                                <Link key={command.to} to={command.to} onClick={closeCommand}>
                                    <strong>{command.label}</strong>
                                    <small>{command.hint}</small>
                                </Link>
                            ))}
                            {!visibleCommands.length && <p>Kein direkter Bereich gefunden. Suche ein Mandat über „Mandate“ oder einen Account über „Kunden“.</p>}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default AppShell;
